import { type GradeCategoryWithLessons } from "@core/grades/types";
import { URLS } from "@core/shared/utils";
import { BaseLink } from "@design-system/components/link";
import { trans } from "@mongez/localization";
import {
  ArrowUpIcon,
  BookOpenIcon,
  FolderClockIcon,
  GraduationCapIcon,
} from "lucide-react";

type CategoryDetailsEmptyStateProps = {
  category: GradeCategoryWithLessons;
};

export function CategoryDetailsEmptyState({
  category,
}: CategoryDetailsEmptyStateProps) {
  const fallbackUrl = category.subject
    ? URLS.subjects.view(category.subject)
    : category.grade
      ? URLS.grades.view(category.grade)
      : URLS.lessons.list;
  const fallbackLabel = category.subject
    ? trans("categoryDetails.backToSubject")
    : category.grade
      ? trans("categoryDetails.backToGrade")
      : trans("categoryDetails.browseLessons");

  return (
    <section className="dark:from-primary-950/30 relative mt-8 overflow-hidden rounded-[2rem] border border-dashed border-primary-200 bg-gradient-to-br from-primary-50 via-white to-violet-50 px-6 py-14 text-center dark:border-primary-900 dark:via-gray-900 dark:to-violet-950/20 sm:px-10">
      <div className="pointer-events-none absolute -end-16 -top-16 h-44 w-44 rounded-full bg-violet-200/40 blur-3xl dark:bg-violet-800/20" />
      <span className="shadow-primary-950/10 relative mx-auto flex h-20 w-20 items-center justify-center rounded-[1.75rem] bg-white text-primary-600 shadow-xl dark:bg-gray-800 dark:text-primary-300">
        <FolderClockIcon className="h-10 w-10" />
      </span>
      <span className="relative mt-6 inline-block text-xs font-black uppercase tracking-wider text-primary-700 dark:text-primary-300">
        {trans("categoryDetails.emptyEyebrow")}
      </span>
      <h2 className="relative mt-2 text-2xl font-black text-gray-950 dark:text-white sm:text-3xl">
        {trans("categoryDetails.emptyTitle")}
      </h2>
      <p className="relative mx-auto mt-3 max-w-xl text-sm leading-7 text-gray-600 dark:text-gray-300 sm:text-base">
        {trans("categoryDetails.emptyMessage", { category: category.name })}
      </p>

      <BaseLink
        className="relative mt-7 inline-flex items-center gap-2 rounded-xl bg-gray-950 px-5 py-3 text-sm font-black text-white shadow-lg transition hover:-translate-y-0.5 hover:bg-primary-700 hover:no-underline dark:bg-white dark:text-gray-950 dark:hover:bg-primary-200"
        to={fallbackUrl}
      >
        {category.subject ? (
          <BookOpenIcon className="h-4 w-4" />
        ) : (
          <GraduationCapIcon className="h-4 w-4" />
        )}
        {fallbackLabel}
        <ArrowUpIcon className="h-4 w-4 -rotate-45 rtl:rotate-45" />
      </BaseLink>
    </section>
  );
}
